import React, { useState, useEffect, useRef } from 'react';
import emailApi from '../services/emailApi';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadNotifications = async () => {
    try {
      const data = await emailApi.getNotifications();
      setNotifications((data.data || []) as unknown as Notification[]);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async () => {
    await emailApi.markNotificationsRead();
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const clearAll = async () => {
    await emailApi.clearNotifications();
    setNotifications([]);
    setUnreadCount(0);
  };

  const timeAgo = (dateStr: string) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'acum';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}z`;
  };

  const typeIcon = (type: string) => {
    switch (type) {
      case 'success': return '\u2705';
      case 'error': return '\u274C';
      case 'warning': return '\u26A0\uFE0F';
      default: return '\u2139\uFE0F';
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen && unreadCount > 0) markRead(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', position: 'relative', padding: '4px 8px' }}
      >
        {'\uD83D\uDD14'}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '-4px', right: '-4px',
            backgroundColor: '#e74c3c', color: 'white', borderRadius: '50%',
            width: '18px', height: '18px', fontSize: '11px', fontWeight: 'bold',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
      {isOpen && (
        <div className="notification-dropdown" style={{
          position: 'absolute', top: '100%', right: 0, width: '340px',
          backgroundColor: 'var(--card-bg, #1e293b)', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          zIndex: 9999, maxHeight: '400px', overflow: 'auto', border: '1px solid var(--border-color, #334155)'
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color, #334155)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: '14px', color: 'var(--text-primary, #e2e8f0)' }}>Notificari</strong>
            {notifications.length > 0 && (
              <button onClick={clearAll} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '12px' }}>Sterge tot</button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontSize: '13px' }}>Nicio notificare</div>
          ) : (
            notifications.slice(0, 20).map(n => (
              <div key={n.id} style={{
                padding: '10px 16px', borderBottom: '1px solid var(--border-color, #334155)',
                backgroundColor: n.is_read ? 'var(--card-bg, #1e293b)' : 'var(--highlight-bg, #1e3a5f)', fontSize: '13px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span>{typeIcon(n.type)} <strong>{n.title}</strong></span>
                  <span style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '11px' }}>{timeAgo(n.created_at)}</span>
                </div>
                <div style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '12px' }}>{n.message}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
